import { createFileRoute, Link } from "@tanstack/react-router";
import { createContext, useContext, useEffect, useState } from "react";

type LandingLang = "ar" | "en";
const LandingLangCtx = createContext<{
  lang: LandingLang;
  setLang: (l: LandingLang) => void;
}>({ lang: "en", setLang: () => {} });
const useLandingLang = () => useContext(LandingLangCtx);
import { Menu, Play, Shield, Heart, Sparkles, Star, Globe, BookOpen, Sprout, Users, ArrowRight, Clock, TreeDeciduous, Apple } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ScrollReveal } from "@/components/landing/ScrollReveal";
import { StoreBadges } from "@/components/landing/StoreBadges";
import { WaitlistForm } from "@/components/landing/WaitlistForm";
import { AppPreviewMockup } from "@/components/landing/AppPreviewMockup";
import { StoryPreview } from "@/components/landing/StoryPreview";
import { CharacterCast } from "@/components/landing/CharacterCast";
import { useLang as useGlobalLang } from "@/hooks/use-language";
import { CharacterImage } from "@/components/CharacterImage";
import { TAGLINE } from "@/lib/brand";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Jozori | جذوري — Growing gently together" },
      {
        name: "description",
        content:
          "Jozori — نكبر بهدوء. A warm, bilingual world helping children ages 4–12 grow gently in values, feelings, and identity. Stories, activities, and small daily moments. Arabic & English.",
      },
      { property: "og:title", content: "Jozori | جذوري — Growing gently together" },
      {
        property: "og:description",
        content:
          "نكبر بهدوء 🤍 · Growing gently together — a calm, family-centered world where children grow in values, feelings, and identity.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://jozori.com/" },
      { property: "og:site_name", content: "Jozori" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Jozori | جذوري — Growing gently together" },
      {
        name: "twitter:description",
        content: "نكبر بهدوء 🤍 · Growing gently together — a warm, family-centered world where children grow rooted in values.",
      },

      { property: "og:image", content: "https://jozori.com/og-image.jpg" },
      { property: "og:image:width", content: "1216" },
      { property: "og:image:height", content: "640" },
      { name: "twitter:image", content: "https://jozori.com/og-image.jpg" },
      { name: "theme-color", content: "#c87a4a" },
    ],
    links: [
      { rel: "canonical", href: "https://jozori.com/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Jozori",
          alternateName: "جذوري",
          url: "https://jozori.com",
          description:
            "A warm, family-centered world where children ages 4–12 grow in values, feelings, and character — bilingual Arabic & English.",

        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.qEn,
            acceptedAnswer: { "@type": "Answer", text: f.aEn },
          })),
        }),
      },
    ],
  }),
  component: Index,
});

const tiers = [
  {
    Icon: Sprout,
    iconBg: "bg-secondary/15 text-secondary",
    ar: "براعم",
    enTitle: "Buds",
    en: "Ages 4–6",
    enLabelAr: "٤–٦ سنوات",
    descEn: "Foundations in sound, color, and gentle Arabic words.",
    descAr: "أساسيات في الأصوات والألوان والكلمات العربية الحانية.",
    arClass: "text-secondary",
    ringClass: "",
  },
  {
    Icon: TreeDeciduous,
    iconBg: "bg-primary/15 text-primary",
    ar: "أغصان",
    enTitle: "Branches",
    en: "Ages 7–9",
    enLabelAr: "٧–٩ سنوات",
    descEn: "Warm stories and gentle daily moments that grow with your child.",
    descAr: "قصص دافئة ولحظات يومية صغيرة تكبر بهدوء مع طفلك.",
    arClass: "text-primary",
    ringClass: "ring-4 ring-primary/20 md:scale-105",
  },
  {
    Icon: Apple,
    iconBg: "bg-accent/15 text-accent",
    ar: "ثمار",
    enTitle: "Fruits",
    en: "Ages 10–12",
    enLabelAr: "١٠–١٢ سنة",
    descEn: "Identity, belonging, and quiet confidence as they grow.",
    descAr: "هوية، وانتماء، وثقة هادئة تكبر مع الطفل.",
    arClass: "text-accent",
    ringClass: "",
  },

];

const activities = [
  {
    ar: "عوالم قصصية",
    en: "Story Worlds",
    descAr: "حكايات دافئة يعيشها طفلك مع شخصيات تكبر معه، وتزرع القيم بهدوء.",
    descEn: "Warm worlds your child steps into — characters that grow with them and plant values quietly.",
  },
  {
    ar: "حكايات تبقى في القلب",
    en: "Stories That Stay in the Heart",
    descAr: "لقطات قصيرة بصوت دافئ، تترك أثرًا صغيرًا كل يوم.",
    descEn: "Short, gently-voiced moments that leave a small mark each day.",
  },
  {
    ar: "لحظات صغيرة تصنع فرقًا",
    en: "Small Daily Moments",
    descAr: "أنشطة هادئة للتلوين والتأمل والرسم — مساحة للطفل ليتنفس ويعبّر.",
    descEn: "Gentle coloring and drawing moments — quiet space for kids to breathe and express.",
  },
  {
    ar: "تحديات يومية لطيفة",
    en: "Gentle Daily Challenges",
    descAr: "مهمة صغيرة كل يوم — لحظة دافئة، لا واجبًا.",
    descEn: "A small invitation each day — a warm moment, never a task.",
  },
  {
    ar: "نحتفل بالنمو",
    en: "Celebrating Growth",
    descAr: "ذكريات صغيرة ومحطات لطيفة، نحتفل بها كعائلة.",
    descEn: "Little memories and gentle milestones, celebrated together as a family.",
  },
  {
    ar: "بدايات لحوارات جميلة",
    en: "Conversation Starters",
    descAr: "أفكار صغيرة لأحاديث عائلية دافئة حول القيم والمشاعر.",
    descEn: "Tiny prompts that open warm family conversations about values and feelings.",
  },
];

const dashboardItems = [
  {
    n: 1,
    bg: "bg-secondary/15",
    fg: "text-secondary",
    tEn: "Watch them grow gently — small moments of language, feelings, and confidence.",
    tAr: "راقبوا نموهم بلطف — لحظات صغيرة في اللغة والمشاعر والثقة.",
  },
  {
    n: 2,
    bg: "bg-primary/15",
    fg: "text-primary",
    tEn: "Choose the stories and worlds that reflect your family's values.",
    tAr: "اختر القصص والعوالم التي تعكس قيم عائلتك.",
  },
  {
    n: 3,
    bg: "bg-accent/15",
    fg: "text-accent",
    tEn: "Set a calm, healthy screen time with gentle automatic limits.",
    tAr: "حدّد وقت شاشة هادئًا وصحيًّا مع إقفال لطيف عند انتهائه.",
  },
];

const howSteps = [
  {
    n: "01",
    titleEn: "Create your family",
    titleAr: "أنشئ عائلتك",
    descEn: "Add a gentle profile for each child — their name, age, and pace. No tests, no pressure.",
    descAr: "أنشئ مساحة لطيفة لكل طفل — اسمه وعمره وإيقاعه الخاص. بلا اختبارات ولا ضغط.",
  },
  {
    n: "02",
    titleEn: "Share a small daily moment",
    titleAr: "شاركوا لحظة يومية صغيرة",
    descEn: "A story, a short clip, or a tiny family activity — chosen with care for their age.",
    descAr: "قصة أو لقطة قصيرة أو نشاط عائلي صغير — مختار بعناية لعمر طفلك.",
  },
  {
    n: "03",
    titleEn: "Grow together, gently",
    titleAr: "انموا معًا بهدوء",
    descEn: "Small steps, lasting roots. A garden of memories that grows with them, week by week.",
    descAr: "خطوات صغيرة… وجذور تدوم. حديقة من الذكريات تنمو معهم أسبوعًا بعد أسبوع.",
  },
];

const testimonials = [
  {
    quoteEn: "My daughter asked me a question in Arabic for the first time. I cried.",
    quoteAr: "سألتني ابنتي سؤالًا بالعربية لأول مرة في حياتها. بكيت من الفرح.",
    name: "Layla H.",
    nameAr: "ليلى ح.",
    roleEn: "Mother of 2 · London",
    roleAr: "أم لطفلين · لندن",
  },
  {
    quoteEn: "Finally, an app that respects our heritage and our parenting standards.",
    quoteAr: "أخيرًا تطبيق يحترم تراثنا ومعاييرنا في تربية أبنائنا.",
    name: "Omar A.",
    nameAr: "عمر أ.",
    roleEn: "Father of 3 · Toronto",
    roleAr: "أب لثلاثة · تورنتو",
  },
  {
    quoteEn: "The stories feel hand-crafted. My boys beg for screen time on Jozori.",
    quoteAr: "القصص تشعرك أنها صُنعت يدويًّا. أولادي يطلبون جذوري بدل أي تطبيق آخر.",
    name: "Mariam S.",
    nameAr: "مريم س.",
    roleEn: "Mother of 2 · Sydney",
    roleAr: "أم لطفلين · سيدني",
  },
];

const trustBadges = [
  { Icon: Shield, labelEn: "COPPA-aligned safety", labelAr: "حماية متوافقة مع COPPA" },
  { Icon: Heart, labelEn: "Made by Arab parents", labelAr: "من صنع آباء عرب" },
  { Icon: Sparkles, labelEn: "Ad-free, ever", labelAr: "بلا إعلانات، أبدًا" },
];

const faqs = [
  {
    qEn: "What ages is Jozori for?",
    qAr: "ما الفئة العمرية المناسبة لجذوري؟",
    aEn: "Jozori is built for children ages 4–12, with three growth tiers: Buds (4–6), Branches (7–9), and Fruits (10–12).",
    aAr: "جذوري مصمَّم للأطفال من ٤ إلى ١٢ سنة، عبر ثلاث مراحل: البراعم (٤–٦)، الأغصان (٧–٩)، والثمار (١٠–١٢).",

  },
  {
    qEn: "Do my kids need to already speak Arabic?",
    qAr: "هل يجب أن يتحدّث أطفالي العربية مسبقًا؟",
    aEn: "Not at all. We start from absolute basics — sounds, letters, and everyday words — and grow with your child.",
    aAr: "أبدًا. نبدأ من الأساسيات — الأصوات والحروف والكلمات اليومية — وننمو مع طفلك خطوة بخطوة.",
  },
  {
    qEn: "Is it safe and ad-free?",
    qAr: "هل هو آمن وخالٍ من الإعلانات؟",
    aEn: "Yes. Jozori has zero ads, zero third-party trackers, and full parental controls. Every piece of content is reviewed by educators.",
    aAr: "نعم. جذوري بلا إعلانات وبلا متتبّعات، مع تحكّم كامل للوالدين. كل محتوى يراجعه مختصّون تربويون.",
  },
  {
    qEn: "Can I cancel anytime?",
    qAr: "هل يمكنني الإلغاء في أي وقت؟",
    aEn: "Absolutely. Manage or cancel your subscription with one tap from the parent dashboard. No questions, no friction.",
    aAr: "بالتأكيد. أوقف أو عدّل اشتراكك بضغطة واحدة من لوحة الوالدين. بدون أسئلة، وبدون تعقيد.",
  },
  {
    qEn: "Will Jozori be on iOS and Android?",
    qAr: "هل سيتوفر جذوري على iOS وAndroid؟",
    aEn: "Yes — native iOS and Android apps are launching soon. The web app works beautifully on every device today.",
    aAr: "نعم — تطبيقات iOS وAndroid قادمة قريبًا، وموقع جذوري يعمل اليوم بانسيابية على كل الأجهزة.",
  },
];

function Index() {
  // Mirror the global LanguageProvider so the legal/support pages and the
  // landing share a single source of truth for the chosen language.
  const global = useGlobalLang();
  const [lang, setLangState] = useState<LandingLang>(global.lang);
  useEffect(() => {
    setLangState(global.lang);
  }, [global.lang]);
  const setLang = (l: LandingLang) => {
    setLangState(l);
    global.setLang(l);
  };
  return (
    <LandingLangCtx.Provider value={{ lang, setLang }}>
      <div className="min-h-dvh bg-background text-foreground" dir={lang === "ar" ? "rtl" : "ltr"}>
        <SiteNav />
        <main id="main">
          <Hero />
          <TrustBar />
          <WhyJozori />
          <StoryPreview lang={lang} />
          <CharacterCast isAr={lang === "ar"} />
          <Tiers />
          <Activities />
          <WhatChildrenReceive />
          <HowItWorks />
          <ParentDashboard />
          <Testimonials />
          <Pricing />
          <Waitlist />
          <FAQ />
          <FinalCTA />
        </main>
        <SiteFooter />
      </div>
    </LandingLangCtx.Provider>
  );
}

/* ---------------- NAV ---------------- */

function LangToggle({ className = "" }: { className?: string }) {
  const { lang, setLang } = useLandingLang();
  return (
    <div
      role="group"
      aria-label="Language"
      className={`inline-flex items-center rounded-full border border-clay/50 bg-card overflow-hidden text-xs font-bold tracking-widest ${className}`}
    >
      <button
        type="button"
        onClick={() => setLang("ar")}
        aria-pressed={lang === "ar"}
        className={`px-3 py-1.5 min-h-9 transition-colors ${lang === "ar" ? "bg-primary text-primary-foreground" : "text-foreground/60 hover:text-primary"}`}
      >
        AR
      </button>
      <span aria-hidden="true" className="text-foreground/25">|</span>
      <button
        type="button"
        onClick={() => setLang("en")}
        aria-pressed={lang === "en"}
        className={`px-3 py-1.5 min-h-9 transition-colors ${lang === "en" ? "bg-primary text-primary-foreground" : "text-foreground/60 hover:text-primary"}`}
      >
        EN
      </button>
    </div>
  );
}

function SiteNav() {
  const [open, setOpen] = useState(false);
  const { lang } = useLandingLang();
  const isAr = lang === "ar";
  const pageLinks = [
    { to: "/about", en: "About", ar: "من نحن" },
    { to: "/story", en: "Our Story", ar: "قصتنا" },
    { to: "/vision", en: "Vision", ar: "الرؤية" },
    { to: "/why", en: "Why Jozori?", ar: "لماذا جذوري؟" },
    { to: "/founder", en: "Founder", ar: "المؤسس" },
    { to: "/safety", en: "Parent Safety", ar: "أمان الأطفال" },
    { to: "/contact", en: "Contact", ar: "تواصل معنا" },
  ] as const;
  const sectionLinks = [
    { href: "#how", en: "How it works", ar: "كيف يعمل" },
    { href: "#pricing", en: "Pricing", ar: "الأسعار" },
    { href: "#faq", en: "FAQ", ar: "الأسئلة" },
  ];
  const portal = isAr ? "بوابة الأهل" : "Parent Portal";
  const menuLabel = isAr ? "القائمة" : "Menu";
  return (
    <nav
      aria-label="Primary"
      className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-clay/40 px-5 py-4 flex items-center justify-between"
    >
      <Link to="/" className="flex items-baseline gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md">
        <span className="font-display-ar text-2xl font-black text-primary">جذوري</span>
        <span className="font-display-en text-lg font-extrabold tracking-tight text-foreground/40">/</span>
        <span className="font-display-en text-lg font-extrabold tracking-tight">JOZORI</span>
      </Link>

      {/* Desktop */}
      <div className="hidden lg:flex items-center gap-5">
        <Link to="/about" className="text-sm font-semibold text-foreground/70 hover:text-primary transition-colors">{isAr ? "من نحن" : "About"}</Link>
        <Link to="/why" className="text-sm font-semibold text-foreground/70 hover:text-primary transition-colors">{isAr ? "لماذا جذوري؟" : "Why Jozori?"}</Link>
        <a href="#pricing" className="text-sm font-semibold text-foreground/70 hover:text-primary transition-colors">{isAr ? "الأسعار" : "Pricing"}</a>
        <Link to="/safety" className="text-sm font-semibold text-foreground/70 hover:text-primary transition-colors">{isAr ? "أمان الأطفال" : "Safety"}</Link>
        <Link to="/contact" className="text-sm font-semibold text-foreground/70 hover:text-primary transition-colors">{isAr ? "تواصل معنا" : "Contact"}</Link>
        <LangToggle />
        <Link
          to="/login"
          className="bg-secondary text-secondary-foreground px-4 py-2 rounded-full text-sm font-bold shadow-sm hover:scale-105 transition-transform inline-flex items-center min-h-11"
        >
          {portal}
        </Link>
      </div>

      {/* Mobile */}
      <div className="lg:hidden flex items-center gap-2">
        <LangToggle />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              aria-label={menuLabel}
              className="size-11 inline-flex items-center justify-center rounded-xl bg-card border border-clay/40 hover:bg-clay/20 transition-colors"
            >
              <Menu className="size-5" aria-hidden="true" />
            </button>
          </SheetTrigger>
          <SheetContent side={isAr ? "left" : "right"} className="bg-background w-[88vw] sm:w-96 flex flex-col">
            <SheetHeader>
              <SheetTitle className="font-display-en text-2xl tracking-tight">{menuLabel}</SheetTitle>
            </SheetHeader>
            <div className="mt-6 flex flex-col gap-1 overflow-y-auto">
              <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-foreground/40">
                {isAr ? "الشركة" : "Company"}
              </p>
              {pageLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="px-4 py-3 rounded-xl text-base font-bold hover:bg-clay/20 transition-colors"
                >
                  {isAr ? l.ar : l.en}
                </Link>
              ))}
              <p className="px-4 pt-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-foreground/40">
                {isAr ? "هذه الصفحة" : "This page"}
              </p>
              {sectionLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="px-4 py-3 rounded-xl text-base font-bold hover:bg-clay/20 transition-colors"
                >
                  {isAr ? l.ar : l.en}
                </a>
              ))}
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="mt-4 bg-primary text-primary-foreground px-5 py-4 rounded-2xl text-center font-bold shadow-press"
              >
                {portal}
              </Link>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
}

/* ---------------- HERO ---------------- */

function Hero() {
  const { lang } = useLandingLang();
  const isAr = lang === "ar";
  return (
    <section className="relative px-5 pt-12 pb-20 overflow-hidden">
      <div aria-hidden="true" className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-primary/10 blur-3xl" />
      <div aria-hidden="true" className="absolute top-40 -right-24 w-80 h-80 rounded-full bg-secondary/10 blur-3xl" />

      <div className="relative max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
        <div className="animate-entrance order-2 md:order-1">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-bold uppercase tracking-widest mb-4">
            <Sparkles className="size-3" aria-hidden="true" />
            {isAr ? "متاح الآن بنسخة مبكرة" : "Now in early access"}
          </span>
          <p
            className="mb-6 inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-foreground/70"
            dir={isAr ? "rtl" : "ltr"}
          >
            <Shield className="size-3.5 text-secondary" aria-hidden="true" />
            <span>
              {isAr
                ? "بلا إعلانات · بلا سلاسل أيام · لا نبيع بياناتك"
                : "No ads · No streaks · No data sold"}
            </span>
          </p>
          <h1 className="mb-6 flex flex-col gap-2">
            {isAr ? (
              <>
                <span dir="rtl" className="font-display-ar text-5xl md:text-7xl text-primary leading-[1.05]">
                  جذوره، هويته، مستقبله.
                </span>
                <span dir="rtl" className="font-display-ar text-2xl md:text-4xl text-foreground leading-tight">
                  انموا مع اللغة والقيم والهوية.
                </span>
              </>
            ) : (
              <>
                <span className="font-display-en text-5xl md:text-7xl text-primary leading-[1.05] tracking-tight">
                  His roots. His identity. His future.
                </span>
                <span className="font-display-en text-3xl md:text-5xl text-foreground leading-tight tracking-tight">
                  Grow with words, values & identity.
                </span>
              </>
            )}
          </h1>
          <p className={`text-base md:text-lg text-foreground/70 mb-6 max-w-[45ch] text-pretty ${isAr ? "font-display-ar text-lg md:text-xl" : ""}`} dir={isAr ? "rtl" : "ltr"}>
            {isAr
              ? "عالم دافئ ينمو مع الطفل والعائلة — قصص، قيم، ولحظات صغيرة تترك أثرًا يبقى."
              : "A warm world that grows with your child and family — stories, values, and small moments that leave a lasting mark."}
          </p>
          {/* Brand tagline — quiet emotional identity line, not a CTA. */}
          <p
            className="mb-8 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-foreground/55"
            dir={isAr ? "rtl" : "ltr"}
          >
            <span className="font-display-ar text-lg md:text-xl text-primary/80">{TAGLINE.ar}</span>
            <span className="text-xs opacity-60">·</span>
            <span className="font-display-en text-sm md:text-base italic">{TAGLINE.en}</span>
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/login"
              className="bg-primary text-primary-foreground px-7 py-4 rounded-2xl font-bold text-base md:text-lg shadow-press active:translate-y-1 active:shadow-none transition-all inline-flex items-center min-h-12"
            >
              {isAr ? "ابدأ التجربة المجانية" : "Start Free Trial"}
            </Link>
            <WatchDemoButton />
          </div>
          <p className="mt-4 text-xs text-foreground/50" dir={isAr ? "rtl" : "ltr"}>
            {isAr
              ? "بدون بطاقة ائتمان · تجربة مجانية ٧ أيام · يمكنك الإلغاء في أي وقت"
              : "No credit card required · 7-day free trial · Cancel anytime"}
          </p>
        </div>
        <div className="relative flex justify-center animate-entrance order-1 md:order-2 [animation-delay:200ms]">
          <div className="animate-float relative w-[280px] md:w-[460px] aspect-square">
            {/* Soft brand glow */}
            <div
              aria-hidden="true"
              className="absolute inset-6 rounded-[4rem] bg-gradient-to-br from-primary/25 via-secondary/20 to-accent/25 blur-2xl"
            />
            {/* Julia — back-right */}
            <Link
              to="/characters/$code"
              params={{ code: "julia" }}
              aria-label={isAr ? "تعرّف على جوليا" : "Meet Julia"}
              className="absolute right-0 top-6 size-[62%] rounded-[3rem] overflow-hidden ring-8 ring-card/70 shadow-2xl bg-clay/20 rotate-3 hover:rotate-0 transition-transform focus-visible:outline-none focus-visible:ring-primary"
              style={{ boxShadow: "0 30px 60px -20px rgba(236,72,153,0.45)" }}
            >
              <CharacterImage
                code="julia"
                alt={isAr ? "جوليا — صديقة طفلك" : "Julia — your child's friend"}
                loading="eager"
                className="size-full object-cover"
                sizes="(min-width: 768px) 280px, 170px"
              />
            </Link>
            {/* Jad — front-left. LCP candidate on the hero. */}
            <Link
              to="/characters/$code"
              params={{ code: "jad" }}
              aria-label={isAr ? "تعرّف على جاد" : "Meet Jad"}
              className="absolute left-0 bottom-0 size-[70%] rounded-[3rem] overflow-hidden ring-8 ring-card/80 shadow-2xl bg-clay/20 -rotate-2 hover:rotate-0 transition-transform focus-visible:outline-none focus-visible:ring-primary"
              style={{ boxShadow: "0 30px 60px -20px rgba(245,158,11,0.55)" }}
            >
              <CharacterImage
                code="jad"
                alt={isAr ? "جاد — صديق طفلك في عالم جذوري" : "Jad — your child's friend in the Jozori world"}
                loading="eager"
                fetchPriority="high"
                className="size-full object-cover"
                sizes="(min-width: 768px) 320px, 195px"
              />
            </Link>
            {/* Caption chip */}
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-card border border-clay/40 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-foreground/70 shadow-md">
              {isAr ? "جاد وجوليا" : "Jad & Julia"}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

function WatchDemoButton() {
  const { lang } = useLandingLang();
  const isAr = lang === "ar";
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="bg-card border-2 border-clay px-7 py-4 rounded-2xl font-bold text-base md:text-lg hover:bg-clay/20 transition-colors inline-flex items-center gap-2 min-h-12"
        >
          <Play className="size-4" aria-hidden="true" /> {isAr ? "شاهد العرض" : "Watch Demo"}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl bg-background">
        <DialogHeader>
          <DialogTitle className="font-display-en text-2xl tracking-tight">
            {isAr ? "نظرة داخل جذوري" : "A peek inside Jozori"}
          </DialogTitle>
          <DialogDescription>
            {isAr ? "مقطع الإطلاق قادم قريبًا. حتى ذلك الحين، إليك لمحة عن الأجواء." : "Our launch trailer is coming soon. In the meantime, here's the vibe."}
          </DialogDescription>
        </DialogHeader>
        <div className="aspect-video rounded-2xl overflow-hidden bg-gradient-to-br from-primary/20 via-clay/30 to-secondary/20 flex items-center justify-center">
          <div className="text-center px-6">
            <div className="text-6xl mb-3" aria-hidden="true">🎬</div>
            <p className="font-display-en text-xl font-bold text-foreground">
              {isAr ? "المقطع التشويقي يصدر مع إطلاق التطبيق" : "Trailer launching with the app"}
            </p>
            <p className="text-sm text-foreground/60 mt-2">
              {isAr ? "اشترك لتكون أوّل من يشاهده." : "Sign up to be the first to watch."}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- TRUST BAR ---------------- */

function TrustBar() {
  const { lang } = useLandingLang();
  const isAr = lang === "ar";
  return (
    <section aria-label="Trust signals" className="px-5 py-6 border-y border-clay/30 bg-card/40">
      <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-center md:justify-between gap-6 text-foreground/70">
        {trustBadges.map(({ Icon, labelEn, labelAr }) => (
          <div key={labelEn} className="flex items-center gap-2 text-sm font-semibold">
            <Icon className="size-4 text-primary" aria-hidden="true" />
            <span>{isAr ? labelAr : labelEn}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} className="size-4 fill-accent text-accent" aria-hidden="true" />
          ))}
          <span className="ml-1 text-sm font-semibold">
            {isAr ? "أحبّتنا أكثر من ٢٬٤٠٠ عائلة" : "Loved by 2,400+ families"}
          </span>
        </div>
      </div>
    </section>
  );
}

/* ---------------- TIERS ---------------- */

function Tiers() {
  const { lang } = useLandingLang();
  const isAr = lang === "ar";
  return (
    <section id="tiers" className="px-5 py-20 bg-clay/15">
      <div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <p className="text-center font-display-en text-xs md:text-sm mb-3 uppercase tracking-[0.3em] text-primary font-bold">
            {isAr ? "تنمو معهم" : "Grows with them"}
          </p>
          <h2 className={`text-center mb-12 tracking-tight ${isAr ? "font-display-ar text-4xl md:text-5xl text-primary" : "font-display-en text-3xl md:text-4xl"}`}>
            {isAr ? "عالم يكبر مع طفلك" : "A world that grows with your child"}
          </h2>
        </ScrollReveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((t, i) => (
            <ScrollReveal key={t.en} delay={i * 100}>
              <div
                className={`bg-card p-7 rounded-3xl border-2 border-clay/30 shadow-xl transition-all cursor-pointer hover:-translate-y-1 hover:shadow-2xl ${t.ringClass}`}
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${t.iconBg}`} aria-hidden="true">
                  <t.Icon className="w-7 h-7" strokeWidth={1.5} />
                </div>
                {isAr ? (
                  <>
                    <h3 dir="rtl" className={`font-display-ar text-3xl mb-1 ${t.arClass}`}>
                      {t.ar}
                    </h3>
                    <p dir="rtl" className="font-display-ar text-lg font-bold mb-3 text-foreground/70">
                      {t.enLabelAr}
                    </p>
                    <p dir="rtl" className="font-display-ar text-base text-foreground/65 leading-relaxed">
                      {t.descAr}
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className={`font-display-en text-2xl font-extrabold mb-1 ${t.arClass}`}>
                      {t.enTitle ?? t.en}
                    </h3>
                    <p className="font-display-en text-sm font-bold mb-3 uppercase tracking-widest text-foreground/50">
                      {t.en}
                    </p>
                    <p className="text-sm text-foreground/60 leading-relaxed">{t.descEn}</p>
                  </>
                )}
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- ACTIVITIES ---------------- */

function Activities() {
  const { lang } = useLandingLang();
  const isAr = lang === "ar";
  return (
    <section id="activities" className="px-5 py-20">
      <div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <p className="font-display-en text-xs md:text-sm mb-3 uppercase tracking-[0.3em] text-secondary font-bold">
            {isAr ? "داخل جذوري" : "Inside Jozori"}
          </p>
          <h2 className={`mb-12 tracking-tight max-w-2xl ${isAr ? "font-display-ar text-4xl md:text-5xl text-primary" : "font-display-en text-3xl md:text-4xl"}`} dir={isAr ? "rtl" : "ltr"}>
            {isAr ? "عالم من القصص، والقيم، واللحظات الدافئة." : "A world of stories, values & warm little moments."}
          </h2>
        </ScrollReveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {activities.map((a, i) => (
            <ScrollReveal key={a.en} delay={(i % 3) * 80}>
              <div className="group h-full p-7 rounded-3xl bg-card border border-clay/40 hover:border-primary/40 hover:shadow-xl transition-all">
                {isAr ? (
                  <>
                    <h3 dir="rtl" className="font-display-ar text-2xl font-black text-primary mb-3 leading-tight">
                      {a.ar}
                    </h3>
                    <p dir="rtl" className="font-display-ar text-base text-foreground/75 leading-relaxed">
                      {a.descAr}
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="font-display-en text-xl font-extrabold mb-2 tracking-tight">{a.en}</h3>
                    <p className="text-sm text-foreground/65 leading-relaxed">{a.descEn}</p>
                  </>
                )}
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- WHAT CHILDREN RECEIVE ---------------- */

const receiveCards = [
  {
    icon: Heart,
    tone: "text-primary",
    bg: "bg-primary/10",
    ar: "لحظات صغيرة تبقى معهم",
    en: "Small moments that stay with them",
    descAr: "ليست دروسًا… بل لحظات دافئة تتسلل بهدوء إلى ذاكرة الطفولة وتبقى.",
    descEn: "Not lessons — gentle moments that slip quietly into childhood memory and stay.",
  },
  {
    icon: BookOpen,
    tone: "text-secondary",
    bg: "bg-secondary/10",
    ar: "قصص وحوارات تنمو داخل البيت",
    en: "Stories & conversations that grow inside the home",
    descAr: "قصص تفتح أحاديث عائلية دافئة حول القيم والمشاعر، بلا قوالب جاهزة.",
    descEn: "Stories that open warm family conversations about values and feelings — never scripted.",
  },
  {
    icon: Sprout,
    tone: "text-accent",
    bg: "bg-accent/10",
    ar: "نمو هادئ بلا ضغط",
    en: "Gentle growth, without pressure",
    descAr: "خطوة صغيرة كل أسبوع — لا اختبارات، لا توبيخ، لا مقارنات.",
    descEn: "One small step each week — no tests, no scolding, no comparisons.",
  },
  {
    icon: Users,
    tone: "text-primary",
    bg: "bg-primary/10",
    ar: "تجربة عائلية دافئة",
    en: "A warm family experience",
    descAr: "تحديات لطيفة يعيشها الطفل مع أسرته — قُرب، لا واجبات.",
    descEn: "Gentle invitations the child lives with their family — closeness, not chores.",
  },
  {
    icon: Sparkles,
    tone: "text-secondary",
    bg: "bg-secondary/10",
    ar: "مساحة آمنة للمشاعر",
    en: "A safe space for feelings",
    descAr: "قصص تساعد الطفل على فهم نفسه ومشاعره، بهدوء وبلا حكم.",
    descEn: "Stories that help a child understand themselves and their feelings — calmly, without judgment.",
  },
  {
    icon: Clock,
    tone: "text-accent",
    bg: "bg-accent/10",
    ar: "إيقاع هادئ يكبر مع طفلك",
    en: "A calm rhythm that grows with your child",
    descAr: "ليست أوراقًا ولا منهجًا… بل رحلة دافئة تكبر بهدوء مع العائلة.",
    descEn: "Not worksheets or a curriculum — a warm journey that grows quietly alongside the family.",
  },
];

function WhatChildrenReceive() {
  const { lang } = useLandingLang();
  const isAr = lang === "ar";
  return (
    <section id="receive" className="px-5 py-20 bg-clay/10">
      <div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <p className="font-display-en text-xs md:text-sm mb-3 uppercase tracking-[0.3em] text-primary font-bold">
            {isAr ? "ما يعيشه الطفل في جذوري" : "What children live in Jozori"}
          </p>
          <h2
            className={`mb-6 tracking-tight max-w-3xl ${isAr ? "font-display-ar text-4xl md:text-5xl text-primary" : "font-display-en text-3xl md:text-4xl"}`}
            dir={isAr ? "rtl" : "ltr"}
          >
            {isAr
              ? "خطوات صغيرة تبني جذورًا عميقة."
              : "Small steps that build deep roots."}
          </h2>
          <p
            className={`max-w-2xl text-foreground/70 mb-12 ${isAr ? "font-display-ar text-lg leading-relaxed" : "text-base leading-relaxed"}`}
            dir={isAr ? "rtl" : "ltr"}
          >
            {isAr
              ? "عالم يكبر بهدوء مع طفلك — لا برنامج دراسي ولا جدول صارم، بل لحظات دافئة تترك أثرًا."
              : "A world that grows quietly with your child — no schedule, no program, just warm moments that leave a mark."}
          </p>
        </ScrollReveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
          {receiveCards.map((c, i) => {
            const Icon = c.icon;
            return (
              <ScrollReveal key={c.en} delay={(i % 3) * 80}>
                <div className="group h-full p-7 rounded-3xl bg-card border border-clay/40 hover:border-primary/40 hover:shadow-xl transition-all">
                  <div className={`size-12 rounded-2xl ${c.bg} ${c.tone} flex items-center justify-center mb-4`}>
                    <Icon className="size-6" />
                  </div>
                  {isAr ? (
                    <>
                      <h3 dir="rtl" className="font-display-ar text-2xl font-black text-primary mb-2 leading-tight">
                        {c.ar}
                      </h3>
                      <p dir="rtl" className="font-display-ar text-base text-foreground/75 leading-relaxed">
                        {c.descAr}
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 className="font-display-en text-xl font-extrabold mb-2 tracking-tight">{c.en}</h3>
                      <p className="text-sm text-foreground/65 leading-relaxed">{c.descEn}</p>
                    </>
                  )}
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        <ScrollReveal delay={120}>
          <figure className="relative max-w-3xl mx-auto p-8 md:p-12 rounded-[2.5rem] bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 border border-clay/40 text-center">
            <Heart className="size-7 text-primary mx-auto mb-4" aria-hidden="true" />
            <blockquote
              className={`${isAr ? "font-display-ar text-2xl md:text-3xl leading-relaxed" : "font-display-en text-xl md:text-2xl leading-snug"} text-foreground/85`}
              dir={isAr ? "rtl" : "ltr"}
            >
              {isAr
                ? "الطفل في جذوري لا يحصل فقط على محتوى… بل يعيش رحلة تنمو داخله بهدوء وثقة ومحبة."
                : "Children in Jozori do not simply receive content — they live a gentle journey that grows within them through warmth, confidence, and connection."}
            </blockquote>
          </figure>
        </ScrollReveal>
      </div>
    </section>
  );
}

/* ---------------- HOW IT WORKS ---------------- */

function HowItWorks() {
  const { lang } = useLandingLang();
  const isAr = lang === "ar";
  return (
    <section id="how" className="px-5 py-20 bg-clay/15">
      <div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <p className="text-center font-display-en text-xs md:text-sm mb-3 uppercase tracking-[0.3em] text-primary font-bold">
            {isAr ? "كيف يعمل" : "How it works"}
          </p>
          <h2 className={`text-center mb-12 tracking-tight ${isAr ? "font-display-ar text-4xl md:text-5xl text-primary" : "font-display-en text-3xl md:text-4xl"}`}>
            {isAr ? "ثلاث خطوات لطيفة" : "Three gentle steps"}
          </h2>
        </ScrollReveal>
        <ol className="grid md:grid-cols-3 gap-6">
          {howSteps.map((s, i) => (
            <ScrollReveal key={s.n} delay={i * 120} as="li">
              <div className="relative h-full p-7 rounded-3xl bg-card border-2 border-clay/30 shadow-lg">
                <div className="font-display-en text-5xl font-black text-primary/20 mb-3">{s.n}</div>
                <h3 className={`font-bold mb-2 ${isAr ? "font-display-ar text-2xl text-primary" : "font-display-en text-xl"}`} dir={isAr ? "rtl" : "ltr"}>
                  {isAr ? s.titleAr : s.titleEn}
                </h3>
                <p className={`text-sm text-foreground/65 ${isAr ? "font-display-ar text-base leading-relaxed" : ""}`} dir={isAr ? "rtl" : "ltr"}>
                  {isAr ? s.descAr : s.descEn}
                </p>
              </div>
            </ScrollReveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ---------------- DASHBOARD ---------------- */

function ParentDashboard() {
  const { lang } = useLandingLang();
  const isAr = lang === "ar";
  return (
    <section className="px-5 py-20">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
        <ScrollReveal>
          <div className="flex justify-center md:justify-start py-6">
            <AppPreviewMockup />
          </div>
        </ScrollReveal>
        <ScrollReveal delay={100}>
          {isAr ? (
            <>
              <h2 dir="rtl" className="font-display-ar text-4xl mb-2 text-primary">
                لوحة الأهل
              </h2>
              <h3 dir="rtl" className="font-display-ar text-2xl md:text-3xl text-foreground/70 mb-8 tracking-tight">
                راحة بال هادئة، بقلوب مطمئنة.
              </h3>
            </>
          ) : (
            <>
              <p className="font-display-en text-xs uppercase tracking-[0.3em] text-primary font-bold mb-3">
                Parent Space
              </p>
              <h2 className="font-display-en text-3xl md:text-4xl mb-8 tracking-tight font-extrabold">
                Calm peace of mind for your family.
              </h2>
            </>
          )}
          <ul className="space-y-5">
            {dashboardItems.map((i) => (
              <li key={i.n} className="flex gap-4">
                <div
                  className={`size-9 rounded-full ${i.bg} ${i.fg} flex items-center justify-center shrink-0 font-bold`}
                  aria-hidden="true"
                >
                  {i.n}
                </div>
                <p className={`pt-1 ${isAr ? "font-display-ar text-lg md:text-xl" : "text-base md:text-lg"}`} dir={isAr ? "rtl" : "ltr"}>
                  {isAr ? i.tAr : i.tEn}
                </p>
              </li>
            ))}
          </ul>
        </ScrollReveal>
      </div>
    </section>
  );
}

/* ---------------- TESTIMONIALS ---------------- */

function Testimonials() {
  const { lang } = useLandingLang();
  const isAr = lang === "ar";
  return (
    <section aria-label="Testimonials" className="px-5 py-20 bg-clay/15">
      <div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <p className="text-center font-display-en text-xs md:text-sm mb-3 uppercase tracking-[0.3em] text-secondary font-bold">
            {isAr ? "يحبّه الأهل" : "Loved by parents"}
          </p>
          <h2 className={`text-center mb-12 tracking-tight ${isAr ? "font-display-ar text-4xl md:text-5xl text-primary" : "font-display-en text-3xl md:text-4xl"}`}>
            {isAr ? "جذور تنبت، وعائلات تزدهر." : "Roots planted, families thriving."}
          </h2>
        </ScrollReveal>
        <div className="grid md:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <ScrollReveal key={t.name} delay={i * 100}>
              <figure className="h-full p-7 rounded-3xl bg-card border border-clay/40 shadow-lg">
                <div className="flex gap-1 mb-4" aria-label="5 out of 5 stars">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} className="size-4 fill-accent text-accent" aria-hidden="true" />
                  ))}
                </div>
                <blockquote
                  className={`leading-snug text-foreground/85 ${isAr ? "font-display-ar text-xl md:text-2xl" : "font-display-en text-lg"}`}
                  dir={isAr ? "rtl" : "ltr"}
                >
                  &ldquo;{isAr ? t.quoteAr : t.quoteEn}&rdquo;
                </blockquote>
                <figcaption className="mt-5 text-sm" dir={isAr ? "rtl" : "ltr"}>
                  <p className="font-bold">{isAr ? t.nameAr : t.name}</p>
                  <p className="text-foreground/55">{isAr ? t.roleAr : t.roleEn}</p>
                </figcaption>
              </figure>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- PRICING ---------------- */

function Pricing() {
  const { lang } = useLandingLang();
  const isAr = lang === "ar";
  const [cycle, setCycle] = useState<"monthly" | "yearly">("yearly");

  const plans = [
    {
      tier: "family" as const,
      nameEn: "Family",
      nameAr: "العائلة",
      taglineEn: "Everything your kids need",
      taglineAr: "كل ما يحتاجه أطفالك",
      monthly: 7.99,
      yearly: 59,
      monthlyEq: 4.92,
      featuresEn: [
        "Full library of stories & activities",
        "Up to 3 child profiles",
        "Parent dashboard",
        "Rewards & badges system",
        "Offline access",
      ],
      featuresAr: [
        "مكتبة كاملة من القصص والأنشطة",
        "حتى ٣ ملفات أطفال",
        "لوحة الوالدين",
        "نظام مكافآت وشارات",
        "وصول بدون إنترنت",
      ],
      highlight: false,
    },
    {
      tier: "premium" as const,
      nameEn: "Premium",
      nameAr: "الذهبية",
      taglineEn: "The full Jozori experience",
      taglineAr: "تجربة جذوري الكاملة",
      monthly: 14.99,
      yearly: 119,
      monthlyEq: 9.92,
      featuresEn: [
        "Everything in Family",
        "Unlimited child profiles",
        "Advanced analytics",
        "Exclusive premium content",
        "Early access features",
        "Premium growth experiences",
      ],
      featuresAr: [
        "كل ميزات العائلة",
        "ملفات أطفال غير محدودة",
        "تحليلات متقدمة",
        "محتوى حصري متميز",
        "وصول مبكر للميزات الجديدة",
        "تجارب نمو متميزة",
      ],
      highlight: true,
    },
  ];

  return (
    <section id="pricing" className="px-5 py-20">
      <div className="max-w-5xl mx-auto text-center">
        <ScrollReveal>
          <p className="font-display-en text-xs uppercase tracking-[0.3em] text-primary font-bold mb-3">
            {isAr ? "الاشتراك" : "Subscription"}
          </p>
          <h2 className={`mb-6 tracking-tight ${isAr ? "font-display-ar text-4xl md:text-5xl text-primary" : "font-display-en text-3xl md:text-4xl"}`}>
            {isAr ? "عائلة واحدة. عالم دافئ ينمو معكم." : "One family. A warm world that grows with you."}
          </h2>
          <p className="text-foreground/60 text-sm md:text-base max-w-xl mx-auto mb-8">
            {isAr ? "اختر الخطة المناسبة لعائلتك. ألغِ في أي وقت." : "Pick the plan that fits your family. Cancel anytime."}
          </p>

          <div className="flex justify-center mb-10">
            <div className="inline-flex p-1 bg-clay/30 rounded-full">
              <button
                onClick={() => setCycle("monthly")}
                className={`px-5 py-2 text-sm font-bold rounded-full transition-colors ${cycle === "monthly" ? "bg-card text-foreground shadow" : "text-foreground/60"}`}
              >
                {isAr ? "شهري" : "Monthly"}
              </button>
              <button
                onClick={() => setCycle("yearly")}
                className={`px-5 py-2 text-sm font-bold rounded-full transition-colors inline-flex items-center gap-2 ${cycle === "yearly" ? "bg-card text-foreground shadow" : "text-foreground/60"}`}
              >
                {isAr ? "سنوي" : "Yearly"}
                <span className="bg-primary text-primary-foreground text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider">
                  {isAr ? "وفّر ٣٤٪" : "Save 34%"}
                </span>
              </button>
            </div>
          </div>
        </ScrollReveal>

        <div className={`grid md:grid-cols-2 gap-5 ${isAr ? "text-right" : "text-left"}`} dir={isAr ? "rtl" : "ltr"}>
          {plans.map((plan, idx) => {
            const price = cycle === "monthly" ? plan.monthly : plan.yearly;
            const suffix = cycle === "monthly" ? (isAr ? "/شهر" : "/mo") : (isAr ? "/سنة" : "/yr");
            return (
              <ScrollReveal key={plan.tier} delay={idx * 100}>
                <div className={`h-full bg-card p-8 rounded-3xl shadow-lg flex flex-col relative ${plan.highlight ? "border-2 border-primary ring-4 ring-primary/15 shadow-2xl" : "border-2 border-clay/30"}`}>
                  {plan.highlight && (
                    <span className={`absolute -top-3 ${isAr ? "left-6" : "right-6"} bg-primary text-primary-foreground text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest`}>
                      {isAr ? "الأكثر شعبية" : "Most Popular"}
                    </span>
                  )}
                  <h3 className={`mb-1 font-bold ${isAr ? "font-display-ar text-2xl text-primary" : "font-display-en text-xl"}`}>
                    {isAr ? plan.nameAr : plan.nameEn}
                  </h3>
                  <p className="text-sm text-foreground/60 mb-4">{isAr ? plan.taglineAr : plan.taglineEn}</p>
                  <p className={`text-4xl font-black mb-1 ${plan.highlight ? "text-primary" : "text-secondary"}`}>
                    ${price}
                    <span className="text-base font-medium text-foreground/50">{suffix}</span>
                  </p>
                  {cycle === "yearly" && (
                    <p className="text-xs text-foreground/50 mb-5">
                      {isAr ? `أي ما يعادل $${plan.monthlyEq}/شهر` : `~ $${plan.monthlyEq}/mo billed yearly`}
                    </p>
                  )}
                  {cycle === "monthly" && <div className="mb-5" />}
                  <ul className={`space-y-2 text-sm mb-6 text-foreground/70 flex-1 ${isAr ? "font-display-ar text-base" : ""}`}>
                    {(isAr ? plan.featuresAr : plan.featuresEn).map((f) => (
                      <li key={f}>✓ {f}</li>
                    ))}
                  </ul>
                  <Link
                    to="/login"
                    className={`w-full py-3 rounded-2xl font-bold transition-all text-center ${plan.highlight ? "bg-primary text-primary-foreground shadow-press active:translate-y-1 active:shadow-none" : "border-2 border-foreground/10 hover:bg-foreground hover:text-background"}`}
                  >
                    {isAr ? `اختر ${plan.nameAr}` : `Choose ${plan.nameEn}`}
                  </Link>
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        <p className="text-xs text-center text-foreground/50 mt-8">
          {isAr
            ? "الدفع آمن عبر Paddle على الويب، وعبر آبل/جوجل داخل التطبيق. تطبق الضرائب حسب بلدك."
            : "Secure billing via Paddle on web, Apple/Google in-app. Taxes apply by region."}
        </p>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */

function FAQ() {
  const { lang } = useLandingLang();
  const isAr = lang === "ar";
  return (
    <section id="faq" className="px-5 py-20 bg-clay/15">
      <div className="max-w-3xl mx-auto">
        <ScrollReveal>
          <p className="text-center font-display-en text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-3">
            {isAr ? "أسئلة" : "Questions"}
          </p>
          <h2 className={`text-center mb-10 tracking-tight ${isAr ? "font-display-ar text-4xl md:text-5xl text-primary" : "font-display-en text-3xl md:text-4xl"}`}>
            {isAr ? "إجابات صنعت بعناية." : "Answered with care."}
          </h2>
        </ScrollReveal>
        <ScrollReveal>
          <Accordion type="single" collapsible className="bg-card rounded-3xl border border-clay/30 shadow-lg px-6">
            {faqs.map((f, i) => (
              <AccordionItem key={f.qEn} value={`item-${i}`} className="border-clay/30 last:border-0">
                <AccordionTrigger
                  className={`hover:no-underline font-bold ${isAr ? "text-right font-display-ar text-lg md:text-xl" : "text-left font-display-en text-base md:text-lg"}`}
                  dir={isAr ? "rtl" : "ltr"}
                >
                  {isAr ? f.qAr : f.qEn}
                </AccordionTrigger>
                <AccordionContent
                  className={`text-foreground/70 leading-relaxed ${isAr ? "font-display-ar text-base md:text-lg text-right" : "text-sm md:text-base"}`}
                  dir={isAr ? "rtl" : "ltr"}
                >
                  {isAr ? f.aAr : f.aEn}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </ScrollReveal>
      </div>
    </section>
  );
}

/* ---------------- FINAL CTA ---------------- */

function FinalCTA() {
  const { lang } = useLandingLang();
  const isAr = lang === "ar";
  return (
    <section className="px-5 py-24">
      <div className="max-w-4xl mx-auto rounded-[2.5rem] bg-gradient-to-br from-primary via-primary to-accent text-primary-foreground p-10 md:p-16 text-center shadow-2xl relative overflow-hidden">
        <div aria-hidden="true" className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
        <div aria-hidden="true" className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
        <div className="relative">
          {isAr ? (
            <>
              <h2 dir="rtl" className="font-display-ar text-4xl md:text-5xl mb-2">
                ابدؤوا رحلة دافئة معًا
              </h2>
              <p dir="rtl" className="font-display-ar text-2xl md:text-3xl tracking-tight mb-6 opacity-90">
                خطوات صغيرة… وجذور تدوم.
              </p>
            </>
          ) : (
            <>
              <h2 className="font-display-en text-4xl md:text-5xl mb-2 tracking-tight font-extrabold">
                Begin a warm journey, together.
              </h2>
              <p className="font-display-en text-2xl md:text-3xl tracking-tight mb-6 opacity-90">
                Small steps. Lasting roots.
              </p>
            </>
          )}
          <p className={`max-w-xl mx-auto opacity-90 mb-8 ${isAr ? "font-display-ar text-lg md:text-xl" : ""}`} dir={isAr ? "rtl" : "ltr"}>
            {isAr
              ? "انضمّوا إلى عائلات تربّي أطفالها على القيم، والمحبة، والثقة — لحظة صغيرة كل يوم."
              : "Join families raising their children in values, warmth, and confidence — one small moment at a time."}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center bg-background text-foreground px-8 py-4 rounded-2xl font-bold text-lg shadow-xl hover:scale-105 transition-transform min-h-12"
          >
            {isAr ? "ابدأ التجربة المجانية" : "Start Free Trial"}
          </Link>
          <div className="mt-10">
            <p className="text-xs uppercase tracking-widest opacity-70 mb-4">
              {isAr ? "التطبيقات الأصلية قادمة قريبًا" : "Native apps launching soon"}
            </p>
            <StoreBadges className="justify-center" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- WHY JOZORI (differentiation) ---------------- */

function WhyJozori() {
  const { lang } = useLandingLang();
  const isAr = lang === "ar";
  const differentiators = [
    {
      icon: Globe,
      ar: "عربي أصيل",
      en: "Arabic-First, Not Translated",
      descAr: "كل قصة وصوت وحرف يُكتب بالعربية أولًا — ثم تُضاف الإنجليزية بعناية. لغة كما تُعاش، لا ترجمة على عجل.",
      descEn: "Every story and sound is authored in Arabic first — then carefully paired with English. Language as it lives, not an afterthought.",
      tone: "text-primary",
      bg: "bg-primary/8",
    },
    {
      icon: Heart,
      ar: "قيم حية",
      en: "Values Woven In, Not Bolted On",
      descAr: "الكرم والصدق واحترام الكبار ليست أوسمة، بل نسيج العوالم التي يكتشفها طفلك مع شخصيات يحبها.",
      descEn: "Generosity, honesty, respect for elders — not reward badges, but the fabric of worlds your child explores with characters they love.",
      tone: "text-secondary",
      bg: "bg-secondary/8",
    },
    {
      icon: Sprout,
      ar: "تنمو معهم",
      en: "Grows With Them, 3 to 12",
      descAr: "من أول حرف إلى أسئلة الهوية في سن المراهقة — جذوري ينمو مع طفلك. اشتراك واحد، عالم واحد، عقد كامل من الانتماء.",
      descEn: "From first letters to tween identity questions — Jozori grows with your child. One subscription, one world, a decade of belonging.",
      tone: "text-accent",
      bg: "bg-accent/10",
    },
    {
      icon: BookOpen,
      ar: "حكايات تلامس القلب",
      en: "Stories That Touch the Heart",
      descAr: "حكايات يكتبها ويرسمها فنانون عرب، تجعل الطفل ينحني نحو الشاشة بدافع المشاعر، لا التكرار.",
      descEn: "Tales hand-crafted by Arab writers and illustrators — emotional stories that make children lean in, not tap through.",
      tone: "text-primary",
      bg: "bg-primary/8",
    },
    {
      icon: Users,
      ar: "من أهلينا، لأهلينا",
      en: "Built by Arab Parents, for Arab Families",
      descAr: "وُلد جذوري من أحاديث العائلات في لندن وتورنتو وسيدني. كل تفصيلة صنعها آباء يعيشون نفس الرحلة.",
      descEn: "Born from family conversations in London, Toronto, and Sydney. Every detail shaped by parents who live this journey.",
      tone: "text-secondary",
      bg: "bg-secondary/8",
    },
    {
      icon: Clock,
      ar: "للأطفال المغتربين",
      en: "For Children of the Diaspora",
      descAr: "حين يكون الجدّ على بُعد مكالمة، والحيّ يتكلم لغة أخرى — يصبح جذوري الجسر، ووطنًا في الجيب.",
      descEn: "When grandparents are a call away and the street speaks another language — Jozori becomes the bridge, a homeland in their pocket.",
      tone: "text-accent",
      bg: "bg-accent/10",
    },
  ];

  return (
    <section aria-label="Why Jozori" className="px-5 py-24 bg-clay/20 relative overflow-hidden">
      <div aria-hidden="true" className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-primary/6 blur-3xl" />
      <div aria-hidden="true" className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-secondary/6 blur-3xl" />

      <div className="relative max-w-6xl mx-auto">
        <ScrollReveal>
          <div className="text-center max-w-4xl mx-auto mb-16">
            <p className="font-display-en text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-5">
              {isAr ? "لماذا جذوري" : "Why Jozori"}
            </p>
            <h2 className="mb-6">
              {isAr ? (
                <span dir="rtl" className="block font-display-ar text-4xl md:text-6xl text-primary leading-[1.1]">
                  لأن أطفالنا المغتربين يستحقون وطنًا في جيبهم.
                </span>
              ) : (
                <span className="block font-display-en text-3xl md:text-5xl text-foreground leading-tight tracking-tight">
                  Because diaspora children deserve a homeland in their pocket.
                </span>
              )}
            </h2>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={80}>
          <div className="max-w-3xl mx-auto mb-20 text-center">
            <blockquote className="relative">
              <span aria-hidden="true" className="absolute -top-4 -left-2 text-6xl text-primary/20 font-display-ar leading-none select-none">&ldquo;</span>
              {isAr ? (
                <p dir="rtl" className="font-display-ar text-lg md:text-2xl text-foreground/80 leading-relaxed">
                  طفلك يفهم العربية على مائدة الطعام — لكنه يجيب بالإنجليزية. لم نجد تطبيقًا يشبه أولادنا، فصنعنا <em className="not-italic font-bold text-primary">جذوري</em>.
                </p>
              ) : (
                <p className="text-lg md:text-xl text-foreground/80 leading-relaxed italic">
                  Your child understands Arabic at the kitchen table — but answers in English. We couldn't find a single app that felt made <em className="not-italic font-semibold text-primary">for our children</em>. So we built one.
                </p>
              )}
              <footer className="mt-5 flex items-center justify-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
                  <Heart className="size-4 text-primary" aria-hidden="true" />
                </div>
                <div className={isAr ? "text-right" : "text-left"}>
                  <p className="text-sm font-bold">
                    {isAr ? "عائلة جذوري المؤسِّسة" : "The Jozori Founding Family"}
                  </p>
                  <p className="text-xs text-foreground/50">London · Toronto · Sydney · Dubai</p>
                </div>
              </footer>
            </blockquote>
          </div>
        </ScrollReveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {differentiators.map((d, i) => {
            const Icon = d.icon;
            return (
              <ScrollReveal key={d.en} delay={i * 80}>
                <div className="group h-full p-7 rounded-3xl bg-card border border-clay/40 hover:border-primary/30 hover:shadow-xl transition-all">
                  <div className={`size-11 rounded-2xl ${d.bg} flex items-center justify-center mb-5`}>
                    <Icon className={`size-5 ${d.tone}`} aria-hidden="true" />
                  </div>
                  {isAr ? (
                    <h3 dir="rtl" className="font-display-ar text-2xl font-black text-primary mb-3 leading-tight">
                      {d.ar}
                    </h3>
                  ) : (
                    <h3 className="font-display-en text-lg font-extrabold text-foreground mb-3 tracking-tight leading-snug">
                      {d.en}
                    </h3>
                  )}
                  {isAr ? (
                    <p dir="rtl" className="font-display-ar text-base text-foreground/75 leading-relaxed">
                      {d.descAr}
                    </p>
                  ) : (
                    <p className="text-sm text-foreground/70 leading-relaxed">{d.descEn}</p>
                  )}
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        <ScrollReveal delay={200}>
          <div className="mt-16 text-center max-w-2xl mx-auto">
            {isAr ? (
              <p dir="rtl" className="font-display-ar text-3xl md:text-4xl text-primary mb-8 leading-snug">
                جذور. هوية. مستقبل. <span className="text-foreground/70">كل ذلك يبدأ هنا.</span>
              </p>
            ) : (
              <p className="font-display-en text-2xl md:text-3xl text-foreground mb-8 tracking-tight font-extrabold">
                Roots. Identity. Future. <span className="text-foreground/60 font-bold">It all starts here.</span>
              </p>
            )}
            <Link
              to="/login"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-2xl font-bold text-base shadow-press active:translate-y-1 active:shadow-none transition-all min-h-12"
            >
              {isAr ? "ابدأ رحلتهم" : "Start Their Journey"}
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}


/* ---------------- WAITLIST ---------------- */

function Waitlist() {
  const { lang } = useLandingLang();
  const isAr = lang === "ar";
  return (
    <section id="waitlist" aria-label="Join the waitlist" className="px-5 py-20 bg-clay/15">
      <div className="max-w-3xl mx-auto text-center">
        <ScrollReveal>
          <span className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-3 py-1 text-xs font-bold uppercase tracking-widest mb-5">
            <Sparkles className="size-3" aria-hidden="true" /> {isAr ? "وصول مبكر" : "Early access"}
          </span>
          <h2 className={`tracking-tight mb-3 ${isAr ? "font-display-ar text-4xl md:text-5xl text-primary" : "font-display-en text-3xl md:text-4xl"}`}>
            {isAr ? "كن أوّل من يزرع جذورهم." : "Be the first to plant their roots."}
          </h2>
          <p className={`text-foreground/65 mb-8 max-w-xl mx-auto ${isAr ? "font-display-ar text-lg" : ""}`} dir={isAr ? "rtl" : "ltr"}>
            {isAr
              ? "اشترك لتصلك أخبار الإطلاق وقصص مجانية وخصم المؤسِّسين عند إطلاق تطبيقاتنا على iOS وAndroid."
              : "Get launch news, free stories, and an early-bird discount when our iOS and Android apps go live."}
          </p>
          <div className="flex justify-center">
            <WaitlistForm source="landing-waitlist" />
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

/* ---------------- FOOTER ---------------- */

function SiteFooter() {
  const { lang } = useLandingLang();
  const isAr = lang === "ar";
  return (
    <footer className="bg-foreground text-background py-14 px-5">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-start gap-10">
        <div>
          <div className="font-display-en text-2xl font-black mb-3 tracking-tighter">
            JOZORI / جذوري
          </div>
          <p className={`max-w-[30ch] opacity-60 text-sm ${isAr ? "font-display-ar text-base" : ""}`} dir={isAr ? "rtl" : "ltr"}>
            {isAr
              ? "نبني الجيل القادم من المتحدّثين الواثقين بالعربية حول العالم."
              : "Building the next generation of confident, Arabic-speaking global citizens."}
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-10 text-sm">
          <div className="space-y-3">
            <h4 className="font-bold text-[10px] uppercase tracking-widest opacity-40">{isAr ? "المنصة" : "Platform"}</h4>
            <nav aria-label="Platform" className="flex flex-col gap-2 font-medium">
              <a href="#activities" className="hover:opacity-100 opacity-80 transition-opacity">{isAr ? "قصص" : "Stories"}</a>
              <a href="#activities" className="hover:opacity-100 opacity-80 transition-opacity">{isAr ? "فيديوهات" : "Videos"}</a>
              <a href="#pricing" className="hover:opacity-100 opacity-80 transition-opacity">{isAr ? "الأسعار" : "Pricing"}</a>
            </nav>
          </div>
          <div className="space-y-3">
            <h4 className="font-bold text-[10px] uppercase tracking-widest opacity-40">{isAr ? "الشركة" : "Company"}</h4>
            <nav aria-label="Company" className="flex flex-col gap-2 font-medium">
              <a href="#how" className="hover:opacity-100 opacity-80 transition-opacity">{isAr ? "كيف يعمل" : "How it works"}</a>
              <a href="#faq" className="hover:opacity-100 opacity-80 transition-opacity">{isAr ? "الأسئلة" : "FAQ"}</a>
              <a href="#tiers" className="hover:opacity-100 opacity-80 transition-opacity">{isAr ? "الفئات العمرية" : "Age tiers"}</a>
            </nav>
          </div>
          <div className="space-y-3">
            <h4 className="font-bold text-[10px] uppercase tracking-widest opacity-40">{isAr ? "الدعم" : "Support"}</h4>
            <nav aria-label="Support" className="flex flex-col gap-2 font-medium">
              <Link to="/safety" className="hover:opacity-100 opacity-80 transition-opacity">{isAr ? "أمان الأطفال" : "Child Safety"}</Link>
              <Link to="/privacy" className="hover:opacity-100 opacity-80 transition-opacity">{isAr ? "الخصوصية" : "Privacy"}</Link>
              <Link to="/terms" className="hover:opacity-100 opacity-80 transition-opacity">{isAr ? "الشروط" : "Terms"}</Link>
              <Link to="/refunds" className="hover:opacity-100 opacity-80 transition-opacity">{isAr ? "الاسترداد" : "Refunds"}</Link>
              <Link to="/login" className="hover:opacity-100 opacity-80 transition-opacity">{isAr ? "تسجيل الدخول" : "Sign in"}</Link>
            </nav>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto mt-16 pt-6 border-t border-background/10 text-[10px] uppercase tracking-widest opacity-30 flex flex-wrap justify-between gap-3">
        <span>{isAr ? "© ٢٠٢٦ جذوري الدولية. صُنع بحب لأطفال المهجر." : "© 2026 Jozori International. Handcrafted for the diaspora."}</span>
        <span>{isAr ? "بُني بحبّ لجذورنا · جذوري" : "Built with love for our roots ·  جذوري"}</span>
      </div>
    </footer>
  );
}
